import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { moderationAPI } from '../services/api';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Card } from '../components/Common/Card';
import EventScheduleDisplay from '../components/Common/EventScheduleDisplay';

// Nur die Felder, die EventScheduleDisplay und die Gruppierung brauchen
const LIST_FIELDS =
  'id,name,location_name,event_type,opening_hours,event_schedule,accessible_from,accessible_until,website';

const LocationActivities = () => {
  const { user, loading: authLoading } = useAuth();
  const isModerator = user?.role === 'moderator' || user?.role === 'admin';
  const [activities, setActivities] = useState([]);
  const [knownLocations, setKnownLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationName, setLocationName] = useState('');
  const debouncedLocationName = useDebouncedValue(locationName);

  // Autovervollstaendigung: nur passende Ortsbezeichnungen holen, nicht alle Aktivitaeten
  useEffect(() => {
    if (!isModerator) return undefined;
    const prefix = debouncedLocationName.trim();
    if (prefix.length < 3) {
      setKnownLocations([]);
      return undefined;
    }
    let cancelled = false;
    moderationAPI
      .getLocationNames({ prefix, limit: 25 })
      .then((response) => {
        if (!cancelled) setKnownLocations(response.data.map((entry) => entry.location_name));
      })
      .catch((error) => console.error('Error loading location names:', error));
    return () => {
      cancelled = true;
    };
  }, [debouncedLocationName, isModerator]);

  // Aktivitaeten erst laden, wenn eine Ortsbezeichnung eingegeben ist
  useEffect(() => {
    if (!isModerator) return undefined;
    const term = debouncedLocationName.trim();
    if (!term) {
      setActivities([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    moderationAPI
      .getAll({ location_name_exact: term, sort: 'name', fields: LIST_FIELDS })
      .then((response) => {
        if (!cancelled) setActivities(response.data);
      })
      .catch((error) => {
        console.error('Error loading activities:', error);
        if (!cancelled) alert('Fehler beim Laden der Aktivitäten: ' + error.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedLocationName, isModerator]);

  // Gruppiere nach der tatsaechlichen Schreibweise der Ortsbezeichnung
  const groups = useMemo(() => {
    const byLocation = new Map();
    activities.forEach((activity) => {
      const loc = activity.location_name;
      if (!loc) return;
      if (!byLocation.has(loc)) byLocation.set(loc, []);
      byLocation.get(loc).push(activity);
    });
    return Array.from(byLocation.entries())
      .sort((a, b) => a[0].localeCompare(b[0], 'de'))
      .map(([loc, acts]) => ({ location: loc, activities: acts }));
  }, [activities]);

  if (authLoading) {
    return (
      <div className="moderation-page">
        <h1>Aktivitäten nach Ort</h1>
        <p>Lädt...</p>
      </div>
    );
  }

  if (!isModerator) {
    return <Navigate to="/" />;
  }

  const term = locationName.trim();
  // Waehrend der Eingabe (Debounce) bzw. der Abfrage nicht vorschnell
  // "nichts gefunden" melden
  const pending = loading || term !== debouncedLocationName.trim();
  const totalActivities = groups.reduce((sum, g) => sum + g.activities.length, 0);

  return (
    <div className="moderation-page">
      <h1>Aktivitäten nach Ort</h1>
      <p style={{ color: '#6c757d', marginTop: '-0.5rem' }}>
        Geben Sie eine Ortsbezeichnung ein, um alle Aktivitäten an diesem Ort mit ihren Terminen zu sehen.
      </p>

      <div style={{ position: 'relative', maxWidth: '420px', margin: '1rem 0 1.5rem' }}>
        <input
          type="text"
          list="location-name-options"
          placeholder="Ortsbezeichnung eingeben..."
          value={locationName}
          onChange={(e) => setLocationName(e.target.value)}
          style={{
            padding: '8px 35px 8px 12px',
            border: '1px solid #ccc',
            borderRadius: 'var(--radius-sm)',
            width: '100%',
            fontSize: '14px',
          }}
        />
        {/* Vorschlaege kommen serverseitig gefiltert (ab 3 Zeichen) */}
        <datalist id="location-name-options">
          {knownLocations.map((loc) => <option key={loc} value={loc} />)}
        </datalist>
        <Building2
          size={18}
          style={{
            position: 'absolute', right: '10px', top: '50%',
            transform: 'translateY(-50%)', color: term ? '#28a745' : '#6c757d', pointerEvents: 'none',
          }}
        />
      </div>

      {!term ? (
        <p style={{ color: '#6c757d' }}>Bitte geben Sie eine Ortsbezeichnung ein.</p>
      ) : pending ? (
        <p>Lädt...</p>
      ) : groups.length === 0 ? (
        <p>Keine Aktivitäten für die Ortsbezeichnung «{term}» gefunden.</p>
      ) : (
        <>
          <p style={{ color: '#6c757d', fontSize: '0.875rem' }}>
            {totalActivities} {totalActivities === 1 ? 'Aktivität' : 'Aktivitäten'} an{' '}
            {groups.length} {groups.length === 1 ? 'Ort' : 'Orten'} gefunden.
          </p>

          {groups.map((group) => (
            <section key={group.location} style={{ marginBottom: '2rem' }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.15rem' }}>
                <Building2 size={20} color="#1e3a5f" />
                {group.location}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {group.activities.map((activity) => (
                  <Card key={activity.id} className="p-3" style={{ minHeight: 'auto' }}>
                    <h3 style={{ margin: '0 0 var(--spacing-2)', fontSize: '1.05rem' }}>{activity.name}</h3>
                    <EventScheduleDisplay
                      eventType={activity.event_type}
                      openingHours={activity.opening_hours}
                      eventSchedule={activity.event_schedule}
                      accessibleFrom={activity.accessible_from}
                      accessibleUntil={activity.accessible_until}
                      website={activity.website}
                    />
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
};

export default LocationActivities;
